<script lang="ts">
	import decorationMenu from "@assets/images/deco/decoration - menu.svg";
	import calendarIcon from "@core/assets/icons/calendar.svg?raw";
	import close from "@core/assets/icons/close.svg?raw";
	import logo from "@core/assets/logo.svg";
	import type { MenuItem } from "@core/schema/MenuItem.js";
	import { lockScroll } from "../../state/lockScroll.svelte.js";
	import { isMenuActive } from "../../utils/isMenuActive.js";
	import Icon from "../common/Icon.svelte";
	import Link from "../common/Link.svelte";
	import { ButtonVariant } from "../common/getButtonClass.js";

	let {
		mobileOpen = $bindable(false),
		/* eslint-disable prefer-const */
		items,
		currentPath = "",
		/* eslint-enable prefer-const */
	}: { mobileOpen?: boolean; items: MenuItem[]; currentPath?: string } = $props();

	let isClosing = $state(false);
	let shouldRender = $state(mobileOpen);
	let isLocked = $state(false);

	function closeMenu(): void {
		isClosing = true;
		setTimeout(() => {
			mobileOpen = false;
			isClosing = false;
			shouldRender = false;
		}, 300);
	}

	$effect(() => {
		if (mobileOpen) {
			shouldRender = true;
		}
	});

	$effect(() => {
		if (mobileOpen && !isLocked) {
			lockScroll.lock = true;
			isLocked = true;
		}
		if (!mobileOpen && isLocked) {
			lockScroll.lock = false;
			isLocked = false;
		}
	});
</script>

{#if shouldRender && (mobileOpen || isClosing)}
	<div
		class="fixed inset-0 z-[60] bg-hazelnut-500 overflow-hidden {isClosing ? 'animate-slideUp' : 'animate-slideDown'}"
		role="dialog"
		aria-modal="true"
	>
		<div class="absolute inset-0">
			<img src={decorationMenu} alt="" class="w-full h-full object-cover" />
		</div>

		<div class="relative flex h-full flex-col items-center py-4 md:py-6 px-4 menu-container overflow-y-auto">
			<div class="flex items-center h-8 md:h-10 flex-shrink-0">
				<Link href="/" class="group">
					<img
						src={logo}
						alt="Napohodu logo"
						class="h-8 md:h-10 transition-transform duration-300 group-hover:scale-110"
					/>
				</Link>
			</div>

			<div class="flex flex-col items-center gap-6 md:gap-8 flex-1 justify-center w-full min-h-0">
				<nav class="flex flex-col items-center gap-4 md:my-2 menu-nav">
					{#each items as item (item.href)}
						<a
							href={item.href}
							class="menu-item group relative flex items-center font-heading font-bold justify-center px-4 py-2.5 sm:px-6 sm:py-3 text-lg sm:text-xl border-2 rounded-full border-transparent transition-all duration-300 {isMenuActive(
								item,
								currentPath,
							)
								? 'bg-copper-300 text-ivory-200 rounded-[2rem] min-w-[10rem] sm:min-w-[12rem] menu-item-active'
								: 'text-ivory-200 hover:text-white hover:border-copper-20-100'}"
							onclick={closeMenu}
						>
							{item.name}
						</a>
					{/each}
				</nav>

				<div class="md:hidden">
					<Link
						href="https://head-spa-na-pohodu.reservio.com/"
						target="_blank"
						variant={ButtonVariant.PrimarySmall}
						iconStart={calendarIcon}
						onclick={closeMenu}
					>
						rezervovat termín
					</Link>
				</div>
			</div>

			<div class="flex items-center h-8 md:h-10 flex-shrink-0">
				<button
					type="button"
					class="text-sm md:text-base uppercase text-ivory-500 hover:text-white duration-300 group transition-colors flex items-center gap-2 md:gap-3"
					onclick={closeMenu}
				>
					<Icon icon={close} class="w-4 h-4 md:w-5 md:h-5 group-hover:rotate-12 duration-300 transition-all" />
					<span class="leading-[0] font-medium tracking-[0.05em]">zavřít menu</span>
				</button>
			</div>
		</div>
	</div>
{/if}

<style>
	@media (min-width: 768px) and (min-height: 1051px) {
		.menu-container {
			padding-top: 3.5rem;
			padding-bottom: 3.5rem;
		}

		.menu-nav {
			gap: 1.5rem;
			margin-top: 1rem;
			margin-bottom: 1rem;
		}

		.menu-item {
			padding-left: 3rem;
			padding-right: 3rem;
			padding-top: 1rem;
			padding-bottom: 1rem;
			font-size: 4rem;
			line-height: 4.8125rem;
		}

		.menu-item-active {
			border-radius: 4rem;
			min-width: 28.9375rem;
		}
	}

	@media (min-width: 768px) and (max-height: 1050px) {
		.menu-container {
			padding-top: 2rem;
			padding-bottom: 2rem;
		}

		.menu-nav {
			gap: 1rem;
			margin-top: 0.5rem;
			margin-bottom: 0.5rem;
		}

		.menu-item {
			padding-left: 2.5rem;
			padding-right: 2.5rem;
			padding-top: 0.75rem;
			padding-bottom: 0.75rem;
			font-size: 3rem;
			line-height: 3.75rem;
		}

		.menu-item-active {
			border-radius: 3.5rem;
			min-width: 24rem;
		}
	}

	@media (max-height: 700px) {
		.menu-container {
			padding-top: 1.25rem;
			padding-bottom: 1.25rem;
		}

		.menu-nav {
			gap: 0.75rem;
			margin-top: 0.5rem;
			margin-bottom: 0.5rem;
		}

		.menu-item {
			padding-left: 2rem;
			padding-right: 2rem;
			padding-top: 0.625rem;
			padding-bottom: 0.625rem;
			font-size: 1.75rem;
			line-height: 2rem;
		}

		.menu-item-active {
			border-radius: 2.5rem;
			min-width: 12rem;
		}
	}
</style>
